import { t } from '../../../../utils/i18n';
import { BreakdownTypes, type BreakdownTypesValues, type PaymentBreakdown } from '../../../../types/PaymentBreakdown';
import { type WidgetProps } from '../../../../types/WidgetProps';
import { type OrderItem } from '../../../../types/OrderItem';
import { getFeeItems, getProductItems } from '../../../../utils/feeItems';
import { getOrderItemsTotal, formatAmount } from '../../../Widget/utils/amount';

export interface GetTransactionInfoParams {
  currency: string;
  paymentAmount: string;
  countryCode: string;
  sessionId: string;
  paymentBreakdown: PaymentBreakdown;
  requestShipping: boolean;
  orderItems?: OrderItem[];
}

export const getTransactionInfo = ({
  currency,
  paymentAmount,
  countryCode,
  sessionId,
  paymentBreakdown,
  requestShipping,
  orderItems,
}: GetTransactionInfoParams): google.payments.api.PaymentDataRequest['transactionInfo'] => {
  return {
    transactionId: sessionId,
    currencyCode: currency,
    countryCode,
    totalPrice: paymentAmount,
    totalPriceLabel: t('paymentSheet.paymentBreakdown.totalPrice'),
    totalPriceStatus: 'ESTIMATED',
    checkoutOption: 'DEFAULT',
    displayItems: getDisplayItems({ paymentBreakdown, requestShipping, orderItems }),
  };
};

export const googlePayDisplayItemType: Record<string, google.payments.api.DisplayItemType> = {
  [BreakdownTypes.Discount]: 'DISCOUNT' as const,
  [BreakdownTypes.Shipping]: 'SHIPPING_OPTION' as const,
  [BreakdownTypes.Tax]: 'TAX' as const,
  itemsTotal: 'SUBTOTAL' as const,
} as const;

export const googlePayDisplayItemTranslationKey: Record<string, string> = {
  [BreakdownTypes.Discount]: 'paymentSheet.paymentBreakdown.discount' as const,
  [BreakdownTypes.Shipping]: 'paymentSheet.paymentBreakdown.shipping' as const,
  [BreakdownTypes.Tax]: 'paymentSheet.paymentBreakdown.tax' as const,
  itemsTotal: 'paymentSheet.paymentBreakdown.itemsTotal' as const,
};

interface GetDisplayItemsParams {
  paymentBreakdown: WidgetProps['paymentBreakdown'];
  requestShipping: boolean;
  orderItems?: OrderItem[];
}

export const getDisplayItems = ({
  paymentBreakdown,
  requestShipping,
  orderItems,
}: GetDisplayItemsParams): google.payments.api.DisplayItem[] => {
  const feeItems = getFeeItems(orderItems);
  const productItems = getProductItems(orderItems);
  const productItemsTotal = getOrderItemsTotal(productItems);

  const displayItems: google.payments.api.DisplayItem[] = [];

  displayItems.push({
    label: t(googlePayDisplayItemTranslationKey.itemsTotal),
    type: googlePayDisplayItemType.itemsTotal,
    price: formatAmount(productItemsTotal),
    status: 'FINAL' as const,
  });

  Object.entries(paymentBreakdown).forEach(([type, amount]: [BreakdownTypesValues, string | undefined]) => {
    const isDigitalProduct = !requestShipping;

    // Don't display shipping item for digital product
    if (isDigitalProduct && type === BreakdownTypes.Shipping) {
      return;
    }

    if (amount) {
      displayItems.push({
        label: t(googlePayDisplayItemTranslationKey[type]),
        type: googlePayDisplayItemType[type],
        price: amount,
        status: 'FINAL' as const,
      });
    }
  });

  // Add fee items as separate line items
  feeItems.forEach((feeItem) => {
    displayItems.push({
      label: feeItem.name,
      type: 'LINE_ITEM' as const,
      price: formatAmount(Number(feeItem.price) * feeItem.quantity),
      status: 'FINAL' as const,
    });
  });

  return displayItems;
};
